import { Sparkles } from "lucide-react";

import type {
  Breakdown,
  CashBucket,
  CashToCloseRender,
} from "../../../lib/agent-types";
import { formatCurrencyFromCents } from "../../../lib/financials";
import { WidgetFrame, WidgetPlaceholderRow } from "../widget-frame";

export function CashToCloseWidget({ payload }: { payload: CashToCloseRender }) {
  const breakdown = payload.breakdown;

  if (!breakdown) {
    return <LegacyFallback payload={payload} />;
  }

  const closingBuckets = breakdown.closingCostEstimate?.buckets ?? [];
  const downPaymentBucket = findCashBucket(breakdown, "down_payment");
  const programFees = findCashBucket(breakdown, "program_upfront_fees");

  return (
    <WidgetFrame data-widget-type="cash_to_close">
      <WidgetPlaceholderRow
        label="Down payment"
        tooltip={cashBucketTooltip(downPaymentBucket, "down_payment")}
        value={formatCurrencyFromCents(breakdown.downPayment)}
      />
      {closingBuckets.length === 0 ? (
        <WidgetPlaceholderRow
          label="Closing costs"
          tooltip={cashBucketTooltip(null, "closing_costs")}
          value={formatCurrencyFromCents(breakdown.closingCosts)}
        />
      ) : null}
      {closingBuckets.map((bucket) => (
        <WidgetPlaceholderRow
          key={bucket.kind}
          label={cashBucketLabel(bucket)}
          tooltip={cashBucketTooltip(bucket)}
          value={formatCurrencyFromCents(bucket.cashDue)}
        />
      ))}
      {breakdown.upfrontFee > 0 ? (
        <WidgetPlaceholderRow
          label="Program Upfront fee"
          tooltip={cashBucketTooltip(programFees, "program_upfront_fees")}
          value={formatCurrencyFromCents(breakdown.upfrontFee)}
        />
      ) : null}
      {(breakdown.financedUpfrontFee ?? 0) > 0 ? (
        <WidgetPlaceholderRow
          label="Financed program fee"
          tooltip="This program fee is included in the loan amount instead of cash due at closing."
          value={formatCurrencyFromCents(breakdown.financedUpfrontFee)}
        />
      ) : null}
      <WidgetPlaceholderRow
        label="Prepaid taxes"
        tooltip={prepaidItemTooltip("property taxes")}
        value={formatCurrencyFromCents(breakdown.prepaids.propertyTaxes)}
      />
      <WidgetPlaceholderRow
        label="Prepaid insurance"
        tooltip={prepaidItemTooltip("homeowners insurance")}
        value={formatCurrencyFromCents(breakdown.prepaids.insurance)}
      />
      <WidgetPlaceholderRow
        label="Prepaid interest"
        tooltip={prepaidItemTooltip("prepaid interest")}
        value={formatCurrencyFromCents(breakdown.prepaids.prepaidInterest)}
      />
      <div className="mt-3 flex items-center justify-between rounded-2xl bg-muted/40 px-3 py-2 text-sm font-semibold">
        <span>Total cash to close</span>
        <span className="font-bold tabular-nums">
          {formatCurrencyFromCents(breakdown.totalCashToClose)}
        </span>
      </div>
      {breakdown.gap ? <GapSection gap={breakdown.gap} /> : <CoveredSection />}
      {breakdown.incentive ? (
        <IncentiveSection incentive={breakdown.incentive} />
      ) : null}
    </WidgetFrame>
  );
}

function GapSection({ gap }: { gap: NonNullable<Breakdown["gap"]> }) {
  const variant = getGapVariant(gap);

  return (
    <div
      className={getGapSectionClassName(variant)}
      data-slot="cash-to-close-gap"
    >
      <p className="text-sm font-medium text-current">
        You need{" "}
        <span className="font-semibold tabular-nums">
          {formatCurrencyFromCents(gap.amount)}
        </span>{" "}
        more than you have saved.
      </p>
      {gap.shortfallMonths && gap.monthlySavingsRate ? (
        <p className="mt-2 text-xs text-current/80">
          At {formatCurrencyFromCents(gap.monthlySavingsRate)}/mo, you'd close
          the gap in ~{gap.shortfallMonths} months.
        </p>
      ) : null}
    </div>
  );
}

function CoveredSection() {
  return (
    <div
      className="mt-3 rounded-2xl border border-success/20 bg-success/8 px-3 py-2 text-success-foreground"
      data-slot="cash-to-close-covered"
    >
      <p className="text-sm font-medium">
        Your current savings cover this estimated cash to close.
      </p>
    </div>
  );
}

function IncentiveSection({
  incentive,
}: {
  incentive: NonNullable<Breakdown["incentive"]>;
}) {
  return (
    <div
      className="mt-3 flex items-start gap-2 rounded-2xl bg-primary-soft px-3 py-2 text-sm text-primary-dark"
      data-slot="cash-to-close-incentive"
    >
      <Sparkles className="mt-0.5 h-4 w-4 flex-shrink-0" />
      <div className="min-w-0">
        <p className="font-medium">{incentive.message}</p>
        {incentive.estimatedReduction > 0 ? (
          <p className="mt-0.5 text-xs opacity-80">
            Up to{" "}
            <span className="font-bold tabular-nums">
              {formatCurrencyFromCents(incentive.estimatedReduction)}
            </span>{" "}
            off cash to close.
          </p>
        ) : null}
      </div>
    </div>
  );
}

function LegacyFallback({ payload }: { payload: CashToCloseRender }) {
  const request = payload.estimate.request;
  const result = payload.estimate.results[0];
  const payment = result?.raw.payment_at_target_price;
  const downPayment = request.financing.down_payment_amount;

  return (
    <WidgetFrame data-widget-type="cash_to_close">
      <WidgetPlaceholderRow
        label="Down payment"
        tooltip="This comes from the estimate request's down-payment amount."
        value={formatCurrencyFromCents(downPayment)}
      />
      <WidgetPlaceholderRow
        label="Upfront fee"
        tooltip="This legacy row comes from the estimate preview when the full cash-to-close breakdown is unavailable."
        value={formatCurrencyFromCents(payment?.upfront_fee)}
      />
    </WidgetFrame>
  );
}

function findCashBucket(breakdown: Breakdown, kind: string): CashBucket | null {
  return breakdown.cashBuckets?.find((bucket) => bucket.kind === kind) ?? null;
}

function cashBucketLabel(bucket: CashBucket) {
  switch (bucket.kind) {
    case "settlement_services":
      return "Settlement / Title services";
    case "prepaids_escrow":
      return "Prepaids / Escrow setup";
    case "program_upfront_fees":
      return "Program Upfront fees";
    case "pricing_fees":
      return "Pricing / Upfront fees";
    default:
      return bucket.label;
  }
}

function prepaidItemTooltip(label: string) {
  return `This is the ${label} portion of the estimated prepaid and Escrow setup.`;
}

function cashBucketTooltip(bucket: CashBucket | null, fallbackKind?: string) {
  const kind = bucket?.kind ?? fallbackKind;

  switch (kind) {
    case "down_payment":
      return "This comes from the active target down-payment plan.";
    case "loan_costs":
      return "This is an estimated share for lender and loan-origination charges.";
    case "government_fees":
      return "This is an estimated share for recording, transfer, and government charges.";
    case "settlement_services":
      return "This is the estimated Title and settlement-service share until a Title quote or lender disclosure is available.";
    case "closing_costs":
      return "This is the ordinary closing-cost estimate before Title or lender disclosures are available.";
    case "prepaids_escrow":
      return "This uses estimated tax Escrow, insurance, and prepaid interest from the payment snapshot and rate assumptions.";
    case "program_upfront_fees":
      return "Program fees come from the loan-product fee schedule; financed portions are tracked but do not increase cash due.";
    case "pricing_fees":
      return "Pricing fees come from the estimate engine and include cash-paid points or other Upfront pricing adjustments.";
    default:
      return "This row comes from the cash-to-close estimate.";
  }
}

function getGapVariant(gap: NonNullable<Breakdown["gap"]>) {
  if ((gap.shortfallMonths ?? 0) >= 12 || gap.amount >= 2_500_000) {
    return "danger" as const;
  }
  return "warning" as const;
}

function getGapSectionClassName(variant: "danger" | "warning") {
  return variant === "danger"
    ? "mt-3 rounded-2xl border border-destructive/20 bg-destructive/8 px-3 py-2 text-destructive"
    : "mt-3 rounded-2xl border border-warning/25 bg-warning/10 px-3 py-2 text-warning-foreground";
}
